/**
 * Context rule curation (SPEC_Kontext_Portal §2).
 *
 * A section under the type list, not a tab of its own — there are three rules.
 *
 * Conditions are never shown as JSON. A reviewer judges a sentence, not an
 * object, and the editor offers selections rather than an expression so that no
 * rule DSL creeps in through the UI.
 *
 * The cap at ±1 and the band saturation are model invariants and appear
 * nowhere — not even read-only. A greyed-out field invites the question and
 * suggests the value is negotiable; one sentence under the list states the
 * consequence instead.
 */

import { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronsUpDown, Loader2, Pencil } from "lucide-react";

import { useModalContext } from "../../components/ModalProvider";
import { useLegalAdministrationAPI } from "../../LegalAdministrationAPI";
import { DAMAGE_TYPES, damageTypeLabel } from "./hazardDisplay";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";

interface Properties {
  refreshSignal: number;
  onChanged: () => void;
}

/** Freigabestand des Regelwerks — dieselbe Semantik wie bei den Gefährdungen. */
const SIGNOFF_VARIANTS: Record<string, "success" | "warning" | "secondary"> = {
  released: "success",
  stale: "warning",
  never: "secondary",
};

const emptyRule = () => ({
  code: "",
  label: "",
  conditions: {
    damageType: "person",
    hazardCodes: [] as string[],
    effectScope: null as string | null,
    usageClassIn: [] as string[],
    personExposureIn: [] as string[],
  },
  delta: 1,
  rationale: "",
});

/** Mehrfachauswahl aus einer geschlossenen Liste (ersetzt die Fluent-ComboBox). */
function MultiSelect(properties: {
  label: string;
  placeholder: string;
  searchPlaceholder: string;
  emptyLabel: string;
  options: { key: string; text: string }[];
  selected: string[];
  onToggle: (key: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const labelId = useId();

  const triggerText =
    properties.selected.length === 0
      ? properties.placeholder
      : properties.options
          .filter((option) => properties.selected.includes(option.key))
          .map((option) => option.text)
          .join(", ");

  return (
    <div className="flex min-w-[240px] flex-1 flex-col gap-1.5">
      <Label id={labelId}>{properties.label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-labelledby={labelId}
            aria-expanded={open}
            className="w-full justify-between font-normal"
          >
            <span className="truncate">{triggerText}</span>
            <ChevronsUpDown aria-hidden className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command>
            <CommandInput placeholder={properties.searchPlaceholder} />
            <CommandList>
              <CommandEmpty>{properties.emptyLabel}</CommandEmpty>
              <CommandGroup>
                {properties.options.map((option) => (
                  <CommandItem
                    key={option.key}
                    value={option.text}
                    className="gap-2"
                    onSelect={() => properties.onToggle(option.key)}
                  >
                    <Checkbox
                      checked={properties.selected.includes(option.key)}
                      tabIndex={-1}
                      className="pointer-events-none"
                    />
                    <span className="min-w-0 break-words">{option.text}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default function ContextRulesPanel(properties: Properties) {
  const { t } = useTranslation();
  const { pushError, pushSuccess } = useModalContext() as any;
  const legalAPI = useLegalAdministrationAPI();
  const fieldId = useId();

  const [data, setData] = useState<any>(null);
  const [hazards, setHazards] = useState<any[]>([]);
  const [editing, setEditing] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [qualification, setQualification] = useState("");
  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);

  const load = async () => {
    try {
      const [rules, catalog] = await Promise.all([legalAPI.getContextRules(), legalAPI.getHazards()]);
      setData(rules);
      setHazards(catalog?.hazards || []);
      setQualification(rules?.permissions?.qualification || "");
    } catch (error) {
      pushError(error);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties.refreshSignal]);

  const hazardsByCode = useMemo(
    () => new Map(hazards.map((hazard: any) => [hazard.code, hazard])),
    [hazards],
  );

  /**
   * Conditions as a sentence. Built from the same fields the editor writes, so
   * the reviewer reads exactly what is stored.
   */
  /** Label for a building-use code, from the vocabulary the server served. */
  const usageClassLabel = (code: string): string =>
    (data?.usageClasses || []).find((entry: any) => entry.usageClass === code)?.name || code;

  const conditionSentence = (rule: any): string => {
    const conditions = rule.conditions || {};
    const parts: string[] = [];

    parts.push(
      conditions.damageType
        ? t("context.rules.sentence.onDamageType", {
            damageType: damageTypeLabel(t, conditions.damageType),
          })
        : t("context.rules.sentence.onAllDamageTypes"),
    );

    if (conditions.hazardCodes?.length) {
      parts.push(
        t("context.rules.sentence.onlyHazards", {
          hazards: conditions.hazardCodes
            .map((code: string) => hazardsByCode.get(code)?.label || code)
            .join(", "),
        }),
      );
    }
    if (conditions.effectScope) {
      parts.push(
        t("context.rules.sentence.onlyEffectScope", {
          scope: t(`context.effectScopeValue.${conditions.effectScope}`),
        }),
      );
    }
    if (conditions.usageClassIn?.length) {
      parts.push(
        t("context.rules.sentence.inUsageClasses", {
          classes: conditions.usageClassIn.map(usageClassLabel).join(", "),
        }),
      );
    }
    if (conditions.personExposureIn?.length) {
      parts.push(
        t("context.rules.sentence.withPersonExposure", {
          exposures: conditions.personExposureIn
            .map((value: string) => t(`context.personExposureValue.${value}`))
            .join(", "),
        }),
      );
    }
    return `${parts.join(" ")}.`;
  };

  const save = async () => {
    if (!editing) return;
    try {
      setSaving(true);
      await legalAPI.saveContextRule(editing);
      setEditing(null);
      await load();
      properties.onChanged();
      pushSuccess(t("context.rules.saved"));
    } catch (error) {
      pushError(error);
    } finally {
      setSaving(false);
    }
  };

  const releaseRuleSet = async () => {
    try {
      setSaving(true);
      await legalAPI.signoffContextRules({ qualification: qualification.trim() || undefined });
      await load();
      properties.onChanged();
      pushSuccess(t("context.rules.releasedSuccess"));
    } catch (error) {
      pushError(error);
    } finally {
      setSaving(false);
    }
  };

  if (!data) {
    return (
      <div className="flex w-full min-w-0 flex-col gap-3">
        <Skeleton className="h-6 w-[220px]" />
        <Skeleton className="h-[180px] w-full" />
      </div>
    );
  }

  const rules: any[] = data.rules || [];

  /** Rough hit preview: how many of the combinations a rule would touch. */
  const matchEstimate = (rule: any): number => {
    const conditions = rule.conditions || {};
    const hazardCount = conditions.hazardCodes?.length
      ? conditions.hazardCodes.length
      : hazards.filter(
          (hazard: any) =>
            (!conditions.damageType || hazard.damageType === conditions.damageType) &&
            (!conditions.effectScope || (hazard.effectScope ?? "local") === conditions.effectScope),
        ).length;
    const usageCount = conditions.usageClassIn?.length || (data.usageClasses || []).length;
    const exposureCount = conditions.personExposureIn?.length || (data.personExposures || []).length;
    return hazardCount * usageCount * exposureCount;
  };

  const toggleInConditions = (field: string, key: string) => {
    const values: string[] = editing.conditions[field] || [];
    setEditing({
      ...editing,
      conditions: {
        ...editing.conditions,
        [field]: values.includes(key)
          ? values.filter((value) => value !== key)
          : [...values, key],
      },
    });
  };

  const codeId = `${fieldId}-code`;
  const labelInputId = `${fieldId}-label`;
  const damageTypeId = `${fieldId}-damage-type`;
  const effectScopeId = `${fieldId}-effect-scope`;
  const deltaId = `${fieldId}-delta`;
  const rationaleId = `${fieldId}-rationale`;

  return (
    <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
      <div className="flex min-w-0 flex-wrap items-center justify-end gap-3">
        <Badge variant={SIGNOFF_VARIANTS[data.signoffState] || "secondary"}>
          {t(`context.rules.signoff.${data.signoffState}`)}
        </Badge>
        {data.permissions?.canSignOff ? (
          <Button
            className="gap-2 max-sm:w-full"
            disabled={saving || rules.length === 0}
            onClick={() => setReleaseDialogOpen(true)}
          >
            {saving ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : null}
            {t("context.rules.release")}
          </Button>
        ) : null}
      </div>

      {rules.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-6 text-center">
          <p className="text-sm font-semibold">{t("context.rules.empty")}</p>
          <p className="max-w-prose text-sm text-muted-foreground">{t("context.rules.emptyHint")}</p>
          {data.permissions?.canCurate && !editing ? (
            <Button variant="outline" size="sm" onClick={() => setEditing(emptyRule())}>
              {t("context.rules.add")}
            </Button>
          ) : null}
        </div>
      ) : (
        <div className="min-w-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[80px]">{t("context.rules.code")}</TableHead>
                <TableHead className="w-[240px]">{t("context.rules.label")}</TableHead>
                <TableHead className="w-[90px]">{t("context.rules.shift")}</TableHead>
                <TableHead>{t("context.rules.conditions")}</TableHead>
                <TableHead className="w-[70px]">
                  <span className="sr-only">{t("context.rules.edit")}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rules.map((rule: any) => (
                <TableRow key={rule.code}>
                  <TableCell className="font-mono text-sm">{rule.code}</TableCell>
                  <TableCell className="min-w-0 break-words">{rule.label}</TableCell>
                  <TableCell>
                    <Badge variant={rule.delta > 0 ? "destructive" : "success"}>
                      {rule.delta > 0 ? "+1" : "−1"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">
                    <span className="min-w-0 break-words">{conditionSentence(rule)}</span>
                    <div className="mt-0.5 min-w-0 break-words text-muted-foreground">
                      {rule.rationale}
                    </div>
                  </TableCell>
                  <TableCell>
                    {data.permissions?.canCurate ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`${t("context.rules.edit")} — ${rule.label}`}
                        title={t("context.rules.edit")}
                        onClick={() => setEditing(JSON.parse(JSON.stringify(rule)))}
                      >
                        <Pencil aria-hidden className="h-4 w-4" />
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* The invariant, stated rather than shown as a disabled control. */}
      <p className="min-w-0 break-words text-xs text-muted-foreground">
        {t("context.rules.capNote")}
      </p>

      {data.permissions?.canCurate && !editing && rules.length > 0 ? (
        <div className="flex flex-wrap">
          <Button
            variant="outline"
            className="max-sm:w-full"
            onClick={() => setEditing(emptyRule())}
          >
            {t("context.rules.add")}
          </Button>
        </div>
      ) : null}

      {editing ? (
        <div className="flex min-w-0 flex-col gap-4 rounded-md border p-4">
          {/* Fixed note above the fields — conjunctive is not obvious from a
              stack of selects, and getting it wrong builds the wrong rule. */}
          <Alert variant="info">
            <AlertDescription>{t("context.rules.conjunctiveHint")}</AlertDescription>
          </Alert>

          <div className="flex min-w-0 flex-wrap gap-4">
            <div className="flex w-[140px] max-w-full flex-col gap-1.5">
              <Label htmlFor={codeId}>{t("context.rules.code")}</Label>
              <Input
                id={codeId}
                value={editing.code}
                onChange={(event) => setEditing({ ...editing, code: event.target.value })}
              />
            </div>
            <div className="flex min-w-[240px] flex-1 flex-col gap-1.5">
              <Label htmlFor={labelInputId}>{t("context.rules.label")}</Label>
              <Input
                id={labelInputId}
                value={editing.label}
                onChange={(event) => setEditing({ ...editing, label: event.target.value })}
              />
            </div>
          </div>

          <div className="flex min-w-0 flex-wrap gap-4">
            <div className="flex min-w-[220px] flex-1 flex-col gap-1.5">
              <Label htmlFor={damageTypeId}>{t("context.rules.onDamageType")}</Label>
              <Select
                value={editing.conditions.damageType ?? "_all"}
                onValueChange={(value: string) =>
                  setEditing({
                    ...editing,
                    conditions: {
                      ...editing.conditions,
                      damageType: value === "_all" ? null : value,
                    },
                  })
                }
              >
                <SelectTrigger id={damageTypeId}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-w-[calc(100vw-2rem)]">
                  <SelectItem value="_all">{t("context.rules.allDamageTypes")}</SelectItem>
                  {DAMAGE_TYPES.map((damageType) => (
                    <SelectItem key={damageType} value={damageType}>
                      {damageTypeLabel(t, damageType)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <MultiSelect
              label={t("context.rules.onlyHazards")}
              placeholder={t("context.rules.allHazards")}
              searchPlaceholder={t("hazard.catalog.searchPlaceholder")}
              emptyLabel={t("hazard.catalog.noMatches")}
              options={hazards.map((hazard: any) => ({
                key: hazard.code,
                text: `${hazard.code} · ${hazard.label}`,
              }))}
              selected={editing.conditions.hazardCodes || []}
              onToggle={(key) => toggleInConditions("hazardCodes", key)}
            />

            <div className="flex min-w-[220px] flex-1 flex-col gap-1.5">
              <Label htmlFor={effectScopeId}>{t("context.rules.onlyEffectScope")}</Label>
              <Select
                value={editing.conditions.effectScope ?? "_all"}
                onValueChange={(value: string) =>
                  setEditing({
                    ...editing,
                    conditions: {
                      ...editing.conditions,
                      effectScope: value === "_all" ? null : value,
                    },
                  })
                }
              >
                <SelectTrigger id={effectScopeId}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-w-[calc(100vw-2rem)]">
                  <SelectItem value="_all">{t("context.rules.allEffectScopes")}</SelectItem>
                  <SelectItem value="local">{t("context.effectScopeValue.local")}</SelectItem>
                  <SelectItem value="building">{t("context.effectScopeValue.building")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex min-w-0 flex-wrap gap-4">
            <MultiSelect
              label={t("context.usageClass")}
              placeholder={t("context.rules.anyValue")}
              searchPlaceholder={t("context.rules.searchValue")}
              emptyLabel={t("context.rules.noValueMatches")}
              options={(data.usageClasses || []).map((entry: any) => ({
                key: entry.usageClass,
                text: entry.name,
              }))}
              selected={editing.conditions.usageClassIn || []}
              onToggle={(key) => toggleInConditions("usageClassIn", key)}
            />

            <MultiSelect
              label={t("context.personExposure")}
              placeholder={t("context.rules.anyValue")}
              searchPlaceholder={t("context.rules.searchValue")}
              emptyLabel={t("context.rules.noValueMatches")}
              options={(data.personExposures || []).map((value: string) => ({
                key: value,
                text: t(`context.personExposureValue.${value}`),
              }))}
              selected={editing.conditions.personExposureIn || []}
              onToggle={(key) => toggleInConditions("personExposureIn", key)}
            />

            <div className="flex min-w-[220px] flex-1 flex-col gap-1.5">
              <Label htmlFor={deltaId}>{t("context.rules.shift")}</Label>
              <Select
                value={String(editing.delta)}
                onValueChange={(value: string) => setEditing({ ...editing, delta: Number(value) })}
              >
                <SelectTrigger id={deltaId}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-w-[calc(100vw-2rem)]">
                  <SelectItem value="1">{t("context.rules.oneBandUp")}</SelectItem>
                  <SelectItem value="-1">{t("context.rules.oneBandDown")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex min-w-0 flex-col gap-1.5">
            <Label htmlFor={rationaleId}>{t("context.rules.rationale")}</Label>
            <Textarea
              id={rationaleId}
              rows={2}
              className="max-w-[720px]"
              placeholder={t("context.rules.rationaleRequired")}
              value={editing.rationale}
              onChange={(event) => setEditing({ ...editing, rationale: event.target.value })}
            />
          </div>

          <p className="min-w-0 break-words text-xs text-muted-foreground">
            {t("context.rules.matchPreview", { count: matchEstimate(editing) })}
            {rules.some(
              (rule: any) =>
                rule.code !== editing.code &&
                JSON.stringify(rule.conditions) === JSON.stringify(editing.conditions),
            ) ? (
              <span className="text-destructive"> · {t("context.rules.duplicateWarning")}</span>
            ) : null}
          </p>

          <div className="flex min-w-0 flex-wrap gap-3">
            <Button
              className="gap-2 max-sm:w-full"
              disabled={saving || !editing.code.trim() || !editing.rationale.trim()}
              onClick={save}
            >
              {saving ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : null}
              {t("context.rules.save")}
            </Button>
            <Button
              variant="outline"
              className="max-sm:w-full"
              disabled={saving}
              onClick={() => setEditing(null)}
            >
              {t("context.rules.cancel")}
            </Button>
          </div>
        </div>
      ) : null}

      {/* Die Freigabe trennt Entwurf von Verbindlichkeit — Ausweg davor. */}
      <AlertDialog open={releaseDialogOpen} onOpenChange={setReleaseDialogOpen}>
        <AlertDialogContent className="w-[calc(100vw-2rem)] sm:max-w-[560px]">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("context.rules.release")}</AlertDialogTitle>
            <AlertDialogDescription className="min-w-0 break-words">
              {t("context.rules.releaseConfirm", { count: rules.length })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="max-sm:w-full">{t("global.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="max-sm:w-full"
              onClick={() => {
                setReleaseDialogOpen(false);
                releaseRuleSet();
              }}
            >
              {t("context.rules.release")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
